import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Alert,
  Box,
  LinearProgress,
  Button,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import api from "../../../api";
import { determineNewLevel } from "../../../utils/levelUtils.js";
import { isAnswerAccurate } from "../../../utils/answerAccuracy.js";
import CardPreprocessor from "../../../utils/CardPreprocessor";

// Import answer input subcomponents
import TextAnswerInput from "./AnswerInputs/TextAnswerInput";
import MCQAnswerInput from "./AnswerInputs/MCQAnswerInput";
import ReorderTokensInput from "./AnswerInputs/ReorderTokensInput";
import AudioQuestion from "./AnswerInputs/AudioQuestion";
import ShadowingInput from "./AnswerInputs/ShadowingInput";

// Import correct sound
import correctSound from "../../../assets/correct.mp3";
import wrongSound from "../../../assets/wrong.mp3";

// Import navigation context
import { useNavigation } from '../../../context/NavigationContext.js'; // Adjust the path as needed

// Icons
import { Close as CloseIcon, ExitToApp as ExitIcon } from '@mui/icons-material';

// Squirrel mascot
import EmbeddableSquirrelMascot from "../../../experiment/Squirrel.js";
// Replace Confetti with Fireworks
import { Fireworks } from "../../animations/FireworksAnimator.js";

// Import our new CSS file
import '../../../styles/review-mode-styles.css';

/** Map numeric levels to descriptive strings */
const LEVEL_DESCRIPTIONS = {
  1: "Apprentice 1",
  2: "Apprentice 2",
  3: "Apprentice 3",
  4: "Guru 1",
  5: "Guru 2",
  6: "Master",
  7: "Enlightened",
  8: "Burned",
};

/** Streak messages based on streak count */
const STREAK_MESSAGES = {
  5: "Nice streak!",
  10: "Great work!",
  15: "Fantastic!",
  20: "Incredible!",
  25: "Unstoppable!",
  30: "Legendary!",
  35: "Phenomenal!",
  40: "Magnificent!",
  45: "Extraordinary!",
  50: "Godlike!",
};

/** Helper function to shuffle an array */
const shuffleArray = (array) => {
  if (!array || array.length <= 1) return array;

  // Fisher-Yates shuffle algorithm
  const shuffled = [...array];

  // Perform multiple passes for better randomization
  for (let pass = 0; pass < 3; pass++) {
    for (let i = shuffled.length - 1; i > 0; i--) {
      // Generate a more random index using crypto if available
      let j;
      if (window.crypto && window.crypto.getRandomValues) {
        const randomBuffer = new Uint32Array(1);
        window.crypto.getRandomValues(randomBuffer);
        j = Math.floor((randomBuffer[0] / (0xffffffff + 1)) * (i + 1));
      } else {
        j = Math.floor(Math.random() * (i + 1));
      }

      // Swap elements
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
  }

  return shuffled;
};

/** Play sound effect */
const playSound = (src) => {
  const audio = new Audio(src);
  audio.play().catch(error => console.error("Error playing sound:", error));
};

const ReviewMode = ({
  token,
  tokenMode = null,
  onReviewComplete,
  initialCards = null,
  rawCards = null, // New prop to help with variant counting
  reviewMetrics = null, // New prop to get sentence variant info
  deck = null,
  courseMode = false,
  courseId = null,
  userId = null,
  onEndEarly = null // New callback for early termination
}) => {
  // Use provided deckId or get from URL params
  const params = useParams();
  const deckId = deck?._id || params.deckId;

  const { currentCourse } = useNavigation();

  // SRS state variables
  const [cards, setCards] = useState([]);
  const [originalCards, setOriginalCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [incorrectQueue, setIncorrectQueue] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [revealedAnswer, setRevealedAnswer] = useState(null);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [waitingToProceed, setWaitingToProceed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [incorrectAttempts, setIncorrectAttempts] = useState({});
  const [sentenceIncorrectAttempts, setSentenceIncorrectAttempts] = useState({}); // NEW: Track sentence-level incorrect attempts
  const [variantAttempts, setVariantAttempts] = useState(new Map());
  const [completedCount, setCompletedCount] = useState(0);
  const countRef = useRef(completedCount);
  const [progress, setProgress] = useState(0);
  const [showArrow, setShowArrow] = useState(false);
  const [arrowDirection, setArrowDirection] = useState("up");
  const [levelChangeInfo, setLevelChangeInfo] = useState(null);
  const [internalUserId, setInternalUserId] = useState(userId);
  const [isRetrying, setIsRetrying] = useState(false);

  // New state for level-up banner
  const [levelUpInfo, setLevelUpInfo] = useState(null);

  const [submittedSentences, setSubmittedSentences] = useState(new Set());

  // Track cards that have been permanently skipped
  const [skippedCards, setSkippedCards] = useState(new Set());

  // Track cards from incorrect queue to prevent level-ups for these cards
  const [cardsFromIncorrectQueue, setCardsFromIncorrectQueue] = useState(new Set());

  // New - Track completed cards to ensure progress never decreases
  const [completedCardIds, setCompletedCardIds] = useState(new Set());

  // New state for confirmation dialog
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);

  // Clean tracking mechanism - track which cards were actually attempted
  const [attemptedCards, setAttemptedCards] = useState(new Set());

  // New state for tracking sentence variants progress
  const [sentenceProgress, setSentenceProgress] = useState({});

  // New streak tracking
  const [currentStreak, setCurrentStreak] = useState(0);
  const [showStreakMessage, setShowStreakMessage] = useState(false);
  const [streakMessage, setStreakMessage] = useState("");
  const [pulseStreak, setPulseStreak] = useState(false);

  // Use a ref as a submission lock to prevent double submissions.
  const submissionLock = useRef(false);

  // Auto-focus reference (for answer inputs if needed)
  const inputRef = useRef(null);

  // Changed from showConfetti to showFireworks for clarity, but keeping the same functionality
  const [showFireworks, setShowFireworks] = useState(false);

  // ==========================================
  // NEW: CLEAN DATA TRANSFORMATION FUNCTIONS
  // ==========================================

  /**
   * Helper to get clean variant type names for ReviewSummary
   */
  const getVariantType = (variant) => {
    if (variant.questionType === 'shadowing') return 'shadowing';
    if (variant.questionType === 'audio') {
      if (variant.audioMode === 'transcribe') return 'audio-transcription';
      if (variant.audioMode === 'translate') return 'audio-translation';
      return 'audio';
    }
    if (variant.questionType === 'tokenReorder') return 'sentence-reconstruction';
    return 'translation';
  };

  /**
   * CLEAN: Transform existing tracking data into clean summary data for ReviewSummary
   */
  const generateCleanSummaryData = () => {
    console.log('Generating clean summary data from existing tracking...');

    const words = [];
    const sentences = [];

    // Get all variant attempts
    const allVariants = Array.from(variantAttempts.values());

    // Group variants by sentence or individual words
    const sentenceGroups = new Map();

    allVariants.forEach(variant => {
      if (variant.sentenceGroup) {
        // This is a sentence variant
        if (!sentenceGroups.has(variant.sentenceGroup)) {
          sentenceGroups.set(variant.sentenceGroup, {
            sentenceId: variant.sentenceGroup,
            text: variant.title || variant.answer || "Unknown sentence",
            translation: variant.answer || variant.question || "Unknown translation",
            variants: [],
            oldLevel: variant.initialLevel || 1,
            newLevel: variant.currentLevel || variant.initialLevel || 1
          });
        }

        sentenceGroups.get(variant.sentenceGroup).variants.push({
          id: variant.uniqueCardId,
          type: getVariantType(variant),
          questionType: variant.questionType,
          audioMode: variant.audioMode,
          status: variant.reviewStatus || 'unreviewed',
          attempts: variant.attempts || 0,
          wasActuallyAttempted: variant.wasActuallyAttempted || false
        });
      } else {
        // This is an individual word
        words.push({
          id: variant.cardId,
          question: variant.question || variant.title || "Unknown",
          answer: variant.answer || "Unknown",
          questionType: variant.questionType || "text",
          status: variant.reviewStatus || 'unreviewed',
          attempts: variant.attempts || 0,
          oldLevel: variant.initialLevel || 1,
          newLevel: variant.currentLevel || variant.initialLevel || 1,
          wasActuallyAttempted: variant.wasActuallyAttempted || false
        });
      }
    });

    // Process sentence groups
    sentenceGroups.forEach((sentenceData, sentenceId) => {
      const variants = sentenceData.variants;
      const totalVariants = variants.length;
      const completedVariants = variants.filter(v => v.wasActuallyAttempted).length;
      const correctVariants = variants.filter(v => v.status === 'correct').length;
      const incorrectVariants = variants.filter(v => v.status === 'incorrect').length;
      const skippedVariants = variants.filter(v => v.status === 'skipped').length;

      // Determine overall status
      let overallStatus = 'unreviewed';
      if (completedVariants > 0) {
        if (incorrectVariants > 0) {
          overallStatus = correctVariants > 0 ? 'mixed' : 'incorrect';
        } else if (skippedVariants > 0) {
          overallStatus = correctVariants > 0 ? 'mixed' : 'skipped';
        } else if (correctVariants > 0) {
          overallStatus = 'correct';
        }
      }

      sentences.push({
        sentenceId,
        text: sentenceData.text,
        translation: sentenceData.translation,
        overallStatus,
        oldLevel: sentenceData.oldLevel,
        newLevel: sentenceData.newLevel,
        totalVariants,
        completedVariants,
        correctVariants,
        incorrectVariants,
        skippedVariants,
        variants,
        wasSubmittedToServer: submittedSentences.has(sentenceId)
      });
    });

    // Calculate overall stats
    const totalItems = words.length + sentences.length;
    const completedWords = words.filter(w => w.wasActuallyAttempted).length;
    const completedSentences = sentences.filter(s => s.completedVariants > 0).length;
    const completedItems = completedWords + completedSentences;

    const correctWords = words.filter(w => w.status === 'correct').length;
    const correctSentences = sentences.filter(s => s.overallStatus === 'correct').length;
    const correctItems = correctWords + correctSentences;

    const incorrectWords = words.filter(w => w.status === 'incorrect').length;
    const incorrectSentences = sentences.filter(s => s.overallStatus === 'incorrect').length;
    const incorrectItems = incorrectWords + incorrectSentences;

    const skippedWords = words.filter(w => w.status === 'skipped').length;
    const skippedSentences = sentences.filter(s => s.overallStatus === 'skipped').length;
    const skippedItems = skippedWords + skippedSentences;

    const unreviewedItems = totalItems - completedItems;
    const accuracy = completedItems > 0 ? Math.round((correctItems / completedItems) * 100) : 0;

    const summaryData = {
      words,
      sentences,
      stats: {
        totalItems,
        completedItems,
        correctItems,
        incorrectItems,
        skippedItems,
        unreviewedItems,
        accuracy,
        // Detailed breakdown
        totalWords: words.length,
        totalSentences: sentences.length,
        correctWords,
        correctSentences,
        incorrectWords,
        incorrectSentences,
        skippedWords,
        skippedSentences
      }
    };

    return summaryData;
  };

  // Extract userId from token if not provided directly
  useEffect(() => {
    if (!internalUserId && token) {
      try {
        const decoded = jwtDecode(token);
        setInternalUserId(decoded.user._id);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [token, internalUserId]);

  /**
   * Process cards to group sentence variants for tracking
   * Each variant is treated independently for tracking purposes
   */
  const processCardsForSentenceTracking = (cardsArray) => {
    const sentenceGroups = {};

    // First pass: identify sentences and count variants
    cardsArray.forEach(card => {
      const sentenceId = card.metadata?.sentenceId;

      // card.uniqueCardId should be pre-assigned by the CardPreprocessor chain
      // from ReviewModeAdapter.
      const uniqueCardId = card.uniqueCardId;

      // For sentence cards, track their variants
      if (sentenceId) {
        if (!sentenceGroups[sentenceId]) {
          // ALWAYS calculate totalVariants from the current cardsArray (expanded list)
          // This is the critical fix.
          let countForThisSentence = 0;
          for (const c of cardsArray) { // cardsArray is the expanded list
            if (c.metadata?.sentenceId === sentenceId) {
              countForThisSentence++;
            }
          }
          const totalVariants = countForThisSentence;

          sentenceGroups[sentenceId] = {
            variantIds: [],
            completedVariantIds: [],
            incorrectVariantIds: [], // NEW: Track incorrect variants
            totalVariants: totalVariants, // Use the actual count, not a hardcoded value
            correctVariants: 0,
            originalLevel: card.currentLevel || 1,
            currentLevel: card.currentLevel || 1
          };
        }

        // Add the unique ID for variant tracking
        sentenceGroups[sentenceId].variantIds.push(uniqueCardId);
      }
    });

    // Initialize sentenceProgress state
    setSentenceProgress(sentenceGroups);
    // setSentenceVariantCounts and variantCounts logic is removed as it was causing the bug.

    // This function's primary role is to set sentenceProgress state (side effect).
    // It receives the already processed and expanded `initialCards`.
    return cardsArray; // Return the original array, as it's not modified here.
  };

  /**
   * Fetch due cards from server on mount or when deckId changes,
   * or use initialCards if provided
   */
  useEffect(() => {
    const setupCards = async () => {
      if (initialCards && initialCards.length > 0) {
        // Process and organize sentence variants
        // processCardsForSentenceTracking will set up sentenceProgress state based on initialCards.
        // initialCards are already the fully expanded and processed list from ReviewModeAdapter.
        processCardsForSentenceTracking(initialCards);
        // REPLACED: Initialize variant tracking map
        const variantMap = new Map();
        initialCards.forEach(card => { // Use initialCards for setting up variantAttempts map
          const uniqueId = card.uniqueCardId || `${card.cardId}-${Math.random().toString(36).substring(2, 9)}`;
          variantMap.set(uniqueId, {
            uniqueCardId: uniqueId,
            cardId: card.cardId,
            questionType: card.questionType,
            audioMode: card.audioMode,
            variantType: card.variantType,
            title: card.title,
            answer: card.answer,
            question: card.question,
            metadata: card.metadata,
            sentenceGroup: card.sentenceGroup || card.metadata?.sentenceId,
            isVariant: card.isVariant || false,
            initialLevel: card.currentLevel || 1,
            currentLevel: card.currentLevel || 1,
            // Tracking fields
            isCorrect: null,
            attempts: 0,
            wasActuallyAttempted: false,
            reviewStatus: 'unreviewed'
          });
        });

        setVariantAttempts(variantMap);

        // Thoroughly shuffle cards to ensure proper randomization
        const shuffledCards = shuffleArray([...initialCards]); // Shuffle the initial (expanded) cards
        console.log('Shuffled cards ', shuffledCards);
        setCards(shuffledCards);
        setOriginalCards(initialCards); // originalCards should also be the full expanded list
      } else if (deckId) {
        // Fetch cards from API
        try {
          const response = await api.get(`/decks/${deckId}/review`);
          let dueCardsRaw = response.data.dueCards || [];

          // IMPORTANT: If ReviewMode is ever used where `initialCards` are not provided
          // and cards are fetched directly, `dueCardsRaw` would need full processing
          // (like in ReviewModeAdapter: CardPreprocessor.processCards, then expandCardsWithVariants)
          // before being passed to processCardsForSentenceTracking.
          // For this fix, we assume `initialCards` is the primary path and is correctly pre-processed.
          // If `dueCardsRaw` were used directly here without full preprocessing, the same bug would appear.
          processCardsForSentenceTracking(dueCardsRaw); // This line is potentially problematic if dueCardsRaw aren't expanded.

          // REPLACED: Initialize variant tracking map
          const variantMap = new Map();
          dueCardsRaw.forEach(card => { // Assuming dueCardsRaw for this path, though it needs expansion first.
            const uniqueId = card.uniqueCardId || `${card.cardId}-${Math.random().toString(36).substring(2, 9)}`;
            variantMap.set(uniqueId, {
              uniqueCardId: uniqueId,
              cardId: card.cardId,
              questionType: card.questionType,
              audioMode: card.audioMode,
              variantType: card.variantType,
              title: card.title,
              answer: card.answer,
              question: card.question,
              metadata: card.metadata,
              sentenceGroup: card.sentenceGroup || card.metadata?.sentenceId,
              isVariant: card.isVariant || false,
              initialLevel: card.currentLevel || 1,
              currentLevel: card.currentLevel || 1,
              // Tracking fields
              isCorrect: null,
              attempts: 0,
              wasActuallyAttempted: false,
              reviewStatus: 'unreviewed'
            });
          });

          setVariantAttempts(variantMap);

          // Thoroughly shuffle cards to ensure proper randomization
          const shuffledCards = shuffleArray([...dueCardsRaw]); // Again, assumes dueCardsRaw are what's intended for review here.
          setCards(shuffledCards);
          setOriginalCards(dueCardsRaw);
        } catch (error) {
          console.error("Error fetching review cards:", error);
          setCards([]);
          setOriginalCards([]);
        }
      } else {
        console.error("No cards provided and no deckId available for fetching");
        setCards([]);
        setOriginalCards([]);
      }

      // Reset component state
      setCurrentIndex(0);
      setIncorrectQueue([]);
      setReviewComplete(false);
      setFeedback(null);
      setRevealedAnswer(null);
      setWaitingToProceed(false);
      setIsSubmitted(false);
      setIncorrectAttempts({});
      setSentenceIncorrectAttempts({}); // Reset sentence-level attempts
      setCompletedCount(0);
      setProgress(0);
      setCurrentStreak(0);
      setShowStreakMessage(false);
      setStreakMessage("");
      setSkippedCards(new Set());
      setCardsFromIncorrectQueue(new Set());
      setCompletedCardIds(new Set()); // Reset completed card IDs
      setAttemptedCards(new Set()); // Reset attempted cards tracking
      submissionLock.current = false; // reset lock on fresh load
      setLevelUpInfo(null); // Reset level up banner
    };

    setupCards();
  }, [initialCards, deckId, token, rawCards, reviewMetrics]);

  /** MODIFIED: Notify parent when review is complete with clean data */
  useEffect(() => {
    if (reviewComplete && onReviewComplete) {
      // Generate clean summary data and pass to parent
      const cleanSummaryData = generateCleanSummaryData();
      onReviewComplete(cleanSummaryData);
    }
  }, [reviewComplete, onReviewComplete, variantAttempts, submittedSentences]);

  /**
   * Global keydown listener for Enter to handle "Proceed" when waiting.
   */
  const handleGlobalKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && waitingToProceed) {
        e.preventDefault();
        handleProceed();
      }
    },
    [waitingToProceed]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [handleGlobalKeyDown]);

  /** Handle streak updates and messaging */
  const updateStreak = (isCorrect) => {
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);

      // Check if we've hit a milestone streak
      Object.entries(STREAK_MESSAGES).forEach(([count, message]) => {
        if (newStreak === parseInt(count)) {
          setStreakMessage(message);
          setShowStreakMessage(true);
          setPulseStreak(true);

          // Hide streak message after 2.5 seconds
          setTimeout(() => {
            setShowStreakMessage(false);
            setPulseStreak(false);
          }, 2500);
        }
      });
    } else {
      // Reset streak on wrong answer
      setCurrentStreak(0);
      setShowStreakMessage(false);
    }
  };

  /** Show arrow animation for 2 seconds. */
  const showArrowFor2s = (direction) => {
    setArrowDirection(direction);
    setShowArrow(true);
    setTimeout(() => setShowArrow(false), 2000);
  };

  /**
   * Check if the answer is correct based on question type and apply the appropriate matching strategy
   */
  const checkAnswer = (currentCard, userAnswer, correctAnswer) => {
    // For token reorder questions, use exact matching only
    if (currentCard.questionType === "tokenReorder") {
      return correctAnswer.toLowerCase() === userAnswer.toLowerCase();
    }

    // For other question types, use more flexible matching including synonyms
    const correctAnswers = correctAnswer
      .split(",")
      .map((ans) => ans.trim().toLowerCase());

    // Check for exact match first
    if (correctAnswers.includes(userAnswer.toLowerCase()) ||
      correctAnswer.toLowerCase() === userAnswer.toLowerCase()) {
      return true;
    }

    // Then check for flexible matching with isAnswerAccurate
    if (isAnswerAccurate(userAnswer.toLowerCase(), correctAnswer.toLowerCase())) {
      return true;
    }

    // Check synonyms if available
    const synonyms = currentCard.answerSynonyms || [];

    return synonyms.some(val =>
      isAnswerAccurate(val.trim().toLowerCase(), userAnswer.toLowerCase())
    );
  };

  /**
   * Mark card as attempted when user actually interacts with it
   */
  const markCardAsAttempted = (uniqueCardId) => {
    setAttemptedCards(prev => new Set([...prev, uniqueCardId]));
  };


  /**
   * MINIMAL FIX: Keep original logic, just handle skipped variants properly
   */
  const updateSentenceProgress = (sentenceId, uniqueCardId, isCorrect, isSkipped = false) => {
    if (!sentenceId) return { shouldSubmit: false };

    const updatedProgress = { ...sentenceProgress };
    const group = updatedProgress[sentenceId];
    if (!group) return { shouldSubmit: false };

    // Initialize arrays if needed
    if (!group.incorrectVariantIds) {
      group.incorrectVariantIds = [];
    }
    if (!group.skippedVariantIds) {
      group.skippedVariantIds = [];
    }

    // Track attempted variants (both correct and incorrect)
    if (!group.completedVariantIds.includes(uniqueCardId)) {
      group.completedVariantIds.push(uniqueCardId);
    }

    // Handle skipped variants separately
    if (isSkipped) {
      // Add to skipped list
      if (!group.skippedVariantIds.includes(uniqueCardId)) {
        group.skippedVariantIds.push(uniqueCardId);
      }
      // Remove from incorrect if previously wrong
      if (group.incorrectVariantIds.includes(uniqueCardId)) {
        const incorrectIndex = group.incorrectVariantIds.indexOf(uniqueCardId);
        group.incorrectVariantIds.splice(incorrectIndex, 1);
      }
    } else {
      // Original logic for correct/incorrect
      if (isCorrect) {
        // Remove from incorrect if previously wrong
        if (group.incorrectVariantIds.includes(uniqueCardId)) {
          const incorrectIndex = group.incorrectVariantIds.indexOf(uniqueCardId);
          group.incorrectVariantIds.splice(incorrectIndex, 1);
        }
      } else {
        // Track as incorrect if not already tracked
        if (!group.incorrectVariantIds.includes(uniqueCardId)) {
          group.incorrectVariantIds.push(uniqueCardId);
        }
      }
    }

    // ORIGINAL CALCULATION: correctVariants = completed - incorrect
    // Skipped variants don't count as incorrect, so this works
    group.correctVariants = group.completedVariantIds.length - group.incorrectVariantIds.length;

    setSentenceProgress(updatedProgress);

    // ORIGINAL LOGIC: Submit when ALL variants are correct AND not submitted yet
    const allVariantsAttempted = group.completedVariantIds.length === group.totalVariants;
    const allVariantsCorrect = group.correctVariants === group.totalVariants;
    const notSubmitted = !submittedSentences.has(sentenceId);
    const shouldSubmit = allVariantsAttempted && allVariantsCorrect && notSubmitted;

    console.log(`Sentence ${sentenceId}: ${group.completedVariantIds.length}/${group.totalVariants} attempted, ${group.correctVariants} correct, ${group.skippedVariantIds.length} skipped, ${group.incorrectVariantIds.length} incorrect, shouldSubmit: ${shouldSubmit}`);

    return { shouldSubmit, allVariantsAttempted, allVariantsCorrect };
  };

  /**
   * FIXED: Improved recalculation of progress for more consistent updates
   * Progress can only increase or stay the same, never decrease
   */
  const recalcProgress = useCallback(() => {
    const total = originalCards.length;
    if (total === 0) {
      setProgress(0);
      return;
    }

    // Use the completedCardIds set to track completed cards
    // This ensures we only count each uniqueCardId once
    const completedCardCount = completedCardIds.size;

    // Update counter ref
    countRef.current = completedCardCount;
    setCompletedCount(completedCardCount);

    // Calculate progress percentage (0-100)
    const calculatedProgress = Math.round((completedCardCount / total) * 100);

    // Set progress, ensuring it never decreases
    setProgress(prev => Math.max(prev, calculatedProgress));
  }, [originalCards.length, completedCardIds]);

  // Call recalcProgress when relevant state changes
  useEffect(() => {
    if (originalCards.length > 0) {
      recalcProgress();
    }
  }, [originalCards.length, completedCardIds, recalcProgress]);

  /**
   * Mark a card as completed by adding its uniqueCardId to the completedCardIds set
   */
  const markCardCompleted = (uniqueCardId) => {
    setCompletedCardIds(prev => new Set([...prev, uniqueCardId]));
  };

  /**
   * Handle answer submission from any input subcomponent.
   * The parameter `answer` can be a string (for text/MCQ) or an array (for tokenReorder).
   * FIXED: CRITICAL - Only submit when all sentence variants are correct
   */
  const handleAnswerSubmit = async (answer) => {
    // Prevent duplicate submission if already processing.
    if (submissionLock.current) return;
    submissionLock.current = true;

    // Validate empty answer for string types.
    if ((!answer && !Array.isArray(answer)) || (typeof answer === "string" && answer.trim() === "")) {
      submissionLock.current = false;
      return;
    }

    setIsSubmitted(true);
    const currentCard = cards[currentIndex];
    if (!currentCard) return;

    // Use uniqueCardId as the primary identifier for tracking
    const cardId = currentCard.cardId; // Still use cardId for API calls
    // In handleAnswerSubmit and handleShadowingSubmit, replace the uniqueCardId line with:
    const uniqueCardId = currentCard.uniqueCardId || `${cardId}-${currentCard.questionType || 'default'}-${currentCard.metadata?.sentenceId || 'individual'}`;
    // CLEAN TRACKING: Mark this card as attempted
    markCardAsAttempted(uniqueCardId);
    const sentenceId = currentCard.metadata?.sentenceId;

    // Determine what the "correct" answer is based on question type
    let correctAnswer = "";
    if (currentCard.questionType === "audio") {
      // For audio questions, use the correctAudioAnswer field
      correctAnswer = (currentCard.correctAudioAnswer || "").trim().toLowerCase();
    } else {
      // For all other questions, use the answer field
      correctAnswer = (currentCard.answer || currentCard.cardId?.answer || "").trim().toLowerCase();
    }

    let userAnswer = "";
    if (Array.isArray(answer)) {
      // For token-reorder, join tokens with space.
      userAnswer = answer.join(" ").trim().toLowerCase();
    } else {
      userAnswer = answer.trim().toLowerCase();
    }

    // Check if the answer is correct using the appropriate strategy
    const isCorrect = checkAnswer(currentCard, userAnswer, correctAnswer);

    let xpGained = 0;
    const oldLevel = currentCard.currentLevel || 1; // Current level of this specific variant/card

    if (sentenceId) {
      const sentenceResult = updateSentenceProgress(sentenceId, uniqueCardId, isCorrect);
      const sentenceInitialLevel = sentenceProgress[sentenceId]?.originalLevel || oldLevel;
      const cardSpecificIncorrectAttempts = incorrectAttempts[uniqueCardId] || 0;

      if (isCorrect) {
        playSound(correctSound);
        setFeedback({ type: "success", message: "Correct!" });
        setRevealedAnswer(null);
        showArrowFor2s("up");
        updateStreak(true);
        markCardCompleted(uniqueCardId);
        setWaitingToProceed(false);


        if (sentenceResult.shouldSubmit) {
          console.log(`SENTENCE SUBMISSION: ${sentenceId} - All variants now correct.`);
          const sentenceHadErrors = (sentenceIncorrectAttempts[sentenceId] || 0) > 0;
          let finalSentenceLevel;

          let sentenceIsFromIncorrectQueue = false;
          const sentenceGroup = sentenceProgress[sentenceId];
          if (sentenceGroup) {
            for (const variantUniqueId of sentenceGroup.variantIds) {
              if (cardsFromIncorrectQueue.has(variantUniqueId)) {
                sentenceIsFromIncorrectQueue = true;
                break;
              }
            }
          }

          if (sentenceIsFromIncorrectQueue) {
            finalSentenceLevel = sentenceInitialLevel;
          } else {
            finalSentenceLevel = determineNewLevel(sentenceInitialLevel, sentenceHadErrors ? 1 : 0, true);
          }

          const result = await sendReviewResult(cardId, sentenceHadErrors ? 1 : 0, true, finalSentenceLevel);
          xpGained = result.xpGained;

          setSubmittedSentences(prev => new Set([...prev, sentenceId]));
          setSentenceProgress(prev => ({
            ...prev,
            [sentenceId]: { ...prev[sentenceId], currentLevel: finalSentenceLevel }
          }));
          handleLevelChangeInfo(sentenceInitialLevel, finalSentenceLevel, true, xpGained);
          updateReviewResult(uniqueCardId, cardId, true, cardSpecificIncorrectAttempts, finalSentenceLevel);
        } else {
          console.log(`SENTENCE PROGRESS: ${sentenceId} - Variant correct, awaiting other variants.`);
          handleLevelChangeInfo(oldLevel, oldLevel, true, 0);
          updateReviewResult(uniqueCardId, cardId, true, cardSpecificIncorrectAttempts, oldLevel);
        }

        removeFromIncorrectQueue(uniqueCardId);
        setTimeout(() => {
          resetForNextCard(uniqueCardId);
          submissionLock.current = false;
        }, 500);

      } else { // Incorrect answer for a sentence variant
        playSound(wrongSound);
        setFeedback({ type: "error", message: "Incorrect!" });
        setRevealedAnswer(correctAnswer);
        showArrowFor2s("down");
        updateStreak(false);
        setWaitingToProceed(true);

        const newVariantAttempts = (incorrectAttempts[uniqueCardId] || 0) + 1;
        setIncorrectAttempts((prev) => ({ ...prev, [uniqueCardId]: newVariantAttempts }));

        setSentenceIncorrectAttempts(prev => {
          const current = prev[sentenceId] || 0;
          return { ...prev, [sentenceId]: Math.min(1, current + 1) };
        });

        console.log(`SENTENCE PROGRESS: ${sentenceId} - Variant incorrect. No API submission.`);
        const displayVariantLevel = determineNewLevel(oldLevel, newVariantAttempts, false);
        handleLevelChangeInfo(oldLevel, displayVariantLevel, false, 0);
        updateReviewAttempts(uniqueCardId, cardId, newVariantAttempts, displayVariantLevel);

        const alreadyQueued = incorrectQueue.some((c) => c.uniqueCardId === uniqueCardId);
        const isSkipped = skippedCards.has(uniqueCardId);
        if (!alreadyQueued && !isSkipped && newVariantAttempts < 3) {
          setIncorrectQueue((prev) => [...prev, currentCard]);
        }
        // submissionLock.current = false;
      }
    } else { // Not a sentence card (standard individual card)
      const cardIncorrectAttempts = incorrectAttempts[uniqueCardId] || 0;
      const isCardFromIncorrectQueue = cardsFromIncorrectQueue.has(uniqueCardId);

      if (isCorrect) {
        playSound(correctSound);
        setFeedback({ type: "success", message: "Correct!" });
        setRevealedAnswer(null);
        showArrowFor2s("up");
        updateStreak(true);
        markCardCompleted(uniqueCardId);
        setWaitingToProceed(false);


        let finalLevel;
        if (isCardFromIncorrectQueue) {
          finalLevel = oldLevel;
          const result = await sendReviewResult(cardId, cardIncorrectAttempts, true, finalLevel);
          xpGained = result.xpGained;
        } else {
          const result = await sendReviewResult(cardId, cardIncorrectAttempts, true);
          finalLevel = result.level;
          xpGained = result.xpGained;
        }
        if (isCardFromIncorrectQueue) cardsFromIncorrectQueue.delete(uniqueCardId);
        handleLevelChangeInfo(oldLevel, finalLevel, true, xpGained);
        updateReviewResult(uniqueCardId, cardId, true, cardIncorrectAttempts, finalLevel);
        removeFromIncorrectQueue(uniqueCardId);
        setTimeout(() => {
          resetForNextCard(uniqueCardId);
          submissionLock.current = false;
        }, 500);
      } else {
        // Incorrect answer for a non-sentence card
        playSound(wrongSound);
        setFeedback({ type: "error", message: "Incorrect!" });
        setRevealedAnswer(correctAnswer);
        showArrowFor2s("down");
        updateStreak(false);
        setWaitingToProceed(true);

        const newCardAttempts = (incorrectAttempts[uniqueCardId] || 0) + 1;
        setIncorrectAttempts((prev) => ({ ...prev, [uniqueCardId]: newCardAttempts }));

        const finalLevel = determineNewLevel(oldLevel, newCardAttempts, false);
        const result = await sendReviewResult(cardId, newCardAttempts, false, finalLevel);
        xpGained = result.xpGained;

        handleLevelChangeInfo(oldLevel, finalLevel, false, xpGained);
        updateReviewAttempts(uniqueCardId, cardId, newCardAttempts, finalLevel);

        const alreadyQueued = incorrectQueue.some((c) => c.uniqueCardId === uniqueCardId);
        const isSkipped = skippedCards.has(uniqueCardId);
        if (!alreadyQueued && !isSkipped && newCardAttempts < 3) {
          setIncorrectQueue((prev) => [...prev, currentCard]);
        }
        // submissionLock.current = false;
      }
    }
    // Recalculate progress after a short delay for both sentence and non-sentence.
    setTimeout(() => recalcProgress(), 50);
  };

  /**
   * Remove a card from the incorrect queue by its uniqueCardId
   */
  const removeFromIncorrectQueue = (uniqueCardId) => {
    setIncorrectQueue(prev =>
      prev.filter(card => card.uniqueCardId !== uniqueCardId)
    );
  };

  // Shadowing submission handler with integration into sentence variant tracking
  const handleShadowingSubmit = async (spokenText, isExactMatch, confidenceScore) => {
    // Prevent duplicate submission if already processing
    if (submissionLock.current) return;
    submissionLock.current = true;

    // Validate empty answer
    if (!spokenText || spokenText.trim() === "") {
      submissionLock.current = false;
      return;
    }

    setIsSubmitted(true);
    const currentCard = cards[currentIndex];
    if (!currentCard) return;

    const cardId = currentCard.cardId;
    const sentenceId = currentCard.metadata?.sentenceId;
    // In handleAnswerSubmit and handleShadowingSubmit, replace the uniqueCardId line with:
    const uniqueCardId = currentCard.uniqueCardId || `${cardId}-${currentCard.questionType || 'default'}-${currentCard.metadata?.sentenceId || 'individual'}`;

    // Mark this card as attempted
    markCardAsAttempted(uniqueCardId);

    // Use the result from the ShadowingInput component
    const isCorrect = isExactMatch;

    let xpGained = 0;
    const oldLevel = currentCard.currentLevel || 1;

    if (sentenceId) {
      const sentenceResult = updateSentenceProgress(sentenceId, uniqueCardId, isCorrect);
      const sentenceInitialLevel = sentenceProgress[sentenceId]?.originalLevel || oldLevel;
      const cardSpecificIncorrectAttempts = incorrectAttempts[uniqueCardId] || 0;

      if (isCorrect) {
        playSound(correctSound);
        setFeedback({ type: "success", message: "Great pronunciation!" });
        setRevealedAnswer(null);
        showArrowFor2s("up");
        setWaitingToProceed(false);
        updateStreak(true);
        markCardCompleted(uniqueCardId);

        if (sentenceResult.shouldSubmit) {
          console.log(`SENTENCE SUBMISSION (Shadowing): ${sentenceId} - All variants now correct.`);
          const sentenceHadErrors = (sentenceIncorrectAttempts[sentenceId] || 0) > 0;
          let finalSentenceLevel;

          let sentenceIsFromIncorrectQueue = false;
          const sentenceGroup = sentenceProgress[sentenceId];
          if (sentenceGroup) {
            for (const variantUniqueId of sentenceGroup.variantIds) {
              if (cardsFromIncorrectQueue.has(variantUniqueId)) {
                sentenceIsFromIncorrectQueue = true;
                break;
              }
            }
          }

          if (sentenceIsFromIncorrectQueue) {
            finalSentenceLevel = sentenceInitialLevel;
          } else {
            finalSentenceLevel = determineNewLevel(sentenceInitialLevel, sentenceHadErrors ? 1 : 0, true);
          }

          const result = await sendReviewResult(cardId, sentenceHadErrors ? 1 : 0, true, finalSentenceLevel);
          xpGained = result.xpGained;

          setSubmittedSentences(prev => new Set([...prev, sentenceId]));
          setSentenceProgress(prev => ({
            ...prev,
            [sentenceId]: { ...prev[sentenceId], currentLevel: finalSentenceLevel }
          }));
          handleLevelChangeInfo(sentenceInitialLevel, finalSentenceLevel, true, xpGained);
          updateReviewResult(uniqueCardId, cardId, true, cardSpecificIncorrectAttempts, finalSentenceLevel);
        } else {
          console.log(`SENTENCE PROGRESS (Shadowing): ${sentenceId} - Variant correct, awaiting others.`);
          handleLevelChangeInfo(oldLevel, oldLevel, true, 0);
          updateReviewResult(uniqueCardId, cardId, true, cardSpecificIncorrectAttempts, oldLevel);
        }

        removeFromIncorrectQueue(uniqueCardId);
        setTimeout(() => {
          resetForNextCard(uniqueCardId);
          submissionLock.current = false;
        }, 500);

      } else { // Incorrect shadowing for a sentence variant
        playSound(wrongSound);
        setFeedback({ type: "error", message: "Try again with your pronunciation!" });
        setRevealedAnswer(null); // Don't reveal answer for shadowing
        showArrowFor2s("down");
        updateStreak(false);
        setWaitingToProceed(true);

        const newVariantAttempts = (incorrectAttempts[uniqueCardId] || 0) + 1;
        setIncorrectAttempts((prev) => ({ ...prev, [uniqueCardId]: newVariantAttempts }));

        setSentenceIncorrectAttempts(prev => {
          const current = prev[sentenceId] || 0;
          return { ...prev, [sentenceId]: Math.min(1, current + 1) };
        });

        console.log(`SENTENCE PROGRESS (Shadowing): ${sentenceId} - Variant incorrect. No API submission.`);
        const displayVariantLevel = determineNewLevel(oldLevel, newVariantAttempts, false);
        handleLevelChangeInfo(oldLevel, displayVariantLevel, false, 0);
        updateReviewAttempts(uniqueCardId, cardId, newVariantAttempts, displayVariantLevel);

        const alreadyQueued = incorrectQueue.some((c) => c.uniqueCardId === uniqueCardId);
        const isSkipped = skippedCards.has(uniqueCardId);
        if (!alreadyQueued && !isSkipped) {
          setIncorrectQueue((prev) => [...prev, currentCard]);
        }
        submissionLock.current = false;
      }
    } else { // Not a sentence card (individual shadowing card)
      const cardIncorrectAttempts = incorrectAttempts[uniqueCardId] || 0;
      const isCardFromIncorrectQueue = cardsFromIncorrectQueue.has(uniqueCardId);

      if (isCorrect) {
        playSound(correctSound);
        setFeedback({ type: "success", message: "Great pronunciation!" });
        setRevealedAnswer(null);
        showArrowFor2s("up");
        updateStreak(true);
        markCardCompleted(uniqueCardId);
        setWaitingToProceed(false);

        let finalLevel;
        if (isCardFromIncorrectQueue) {
          finalLevel = oldLevel;
          const result = await sendReviewResult(cardId, cardIncorrectAttempts, true, finalLevel);
          xpGained = result.xpGained;
        } else {
          const result = await sendReviewResult(cardId, cardIncorrectAttempts, true);
          finalLevel = result.level;
          xpGained = result.xpGained;
        }
        if (isCardFromIncorrectQueue) cardsFromIncorrectQueue.delete(uniqueCardId);
        handleLevelChangeInfo(oldLevel, finalLevel, true, xpGained);
        updateReviewResult(uniqueCardId, cardId, true, cardIncorrectAttempts, finalLevel);
        removeFromIncorrectQueue(uniqueCardId);
        setTimeout(() => {
          resetForNextCard(uniqueCardId);
          submissionLock.current = false;
        }, 500);
      } else {
        // Incorrect shadowing for a non-sentence card
        playSound(wrongSound);
        setFeedback({ type: "error", message: "Try again with your pronunciation!" });
        setRevealedAnswer(null); // Don't reveal answer for shadowing
        showArrowFor2s("down");
        updateStreak(false);
        setWaitingToProceed(true);

        const newCardAttempts = (incorrectAttempts[uniqueCardId] || 0) + 1;
        setIncorrectAttempts((prev) => ({ ...prev, [uniqueCardId]: newCardAttempts }));

        const finalLevel = determineNewLevel(oldLevel, newCardAttempts, false);
        const result = await sendReviewResult(cardId, newCardAttempts, false, finalLevel);
        xpGained = result.xpGained;

        handleLevelChangeInfo(oldLevel, finalLevel, false, xpGained);
        updateReviewAttempts(uniqueCardId, cardId, newCardAttempts, finalLevel);

        const alreadyQueued = incorrectQueue.some((c) => c.uniqueCardId === uniqueCardId);
        const isSkipped = skippedCards.has(uniqueCardId);
        if (!alreadyQueued && !isSkipped) {
          setIncorrectQueue((prev) => [...prev, currentCard]);
        }
        submissionLock.current = false;
      }
    }
    // Recalculate progress after a short delay
    setTimeout(() => recalcProgress(), 50);
  };

  /** After an incorrect answer, user may click "Proceed" or press Enter. */
  const handleProceed = () => {
    setWaitingToProceed(false);
    setIsSubmitted(false);
    setFeedback(null);
    setRevealedAnswer(null);
    setLevelChangeInfo(null);
    submissionLock.current = false; // Unlock submission.

    // Only proceed to next card if not retrying
    const isRetrying = !!document.querySelector('[data-retry-button="true"]');
    if (!isRetrying) {
      proceedToNextCard();
    } else {
      setIsRetrying(false);
    }
  };

  /** Move on to the next card or finish the review. */
  const proceedToNextCard = () => {
    console.log("🟢 proceedToNextCard called : waiting to proceed : ", waitingToProceed);
    console.log("🟢 currentIndex:", currentIndex);
    if (currentIndex + 1 < cards.length) {
      setCurrentIndex((idx) => idx + 1);
      return;
    }
    if (incorrectQueue.length > 0) {
      const shuffledIncorrectQueue = shuffleArray(incorrectQueue);

      // Track which cards are from the incorrect queue using their uniqueCardId
      const newCardsFromIncorrectQueue = new Set(
        shuffledIncorrectQueue.map(card => card.uniqueCardId || `${card.cardId}-${Math.random().toString(36).substring(2, 9)}`)
      );
      setCardsFromIncorrectQueue(newCardsFromIncorrectQueue);

      setCards(shuffledIncorrectQueue);
      setIncorrectQueue([]);
      setCurrentIndex(0);
    } else {
      setReviewComplete(true);
      setCards([]);
    }
  };

  /** Reset UI state and proceed after a card is answered. */
  const resetForNextCard = (cardId) => {
    // For text-based answers, the answer input subcomponent resets via key change.
    setFeedback(null);
    setIsSubmitted(false);
    setIncorrectAttempts((prev) => {
      const updated = { ...prev };
      delete updated[cardId];
      return updated;
    });
    setLevelChangeInfo(null);
    proceedToNextCard();
  };

  /** 
   * FIXED: Improved level change info display to always show level changes
   * Display floating level-change info if there's an actual change.
   */
  const handleLevelChangeInfo = (oldLevel, newLevel, wasCorrect, xpGained = 0) => {
    // Check if both levels are valid (could be null or undefined)
    const validOldLevel = oldLevel === null || oldLevel === undefined ? 1 : oldLevel;
    const validNewLevel = newLevel === null || newLevel === undefined ? validOldLevel : newLevel;

    // Only show animation if there's an actual level change or if it was correct with same level
    if (validNewLevel !== validOldLevel || wasCorrect) {
      const oldLevelDesc = LEVEL_DESCRIPTIONS[validOldLevel] || `Level ${validOldLevel}`;
      const newLevelDesc = LEVEL_DESCRIPTIONS[validNewLevel] || `Level ${validNewLevel}`;
      const isPositive = validNewLevel > validOldLevel || wasCorrect;

      // IMPROVED: Add more descriptive messages based on level change
      let levelMessage = '';

      if (validNewLevel > validOldLevel) {
        levelMessage = 'Level up!';
      } else if (validNewLevel < validOldLevel) {
        levelMessage = 'Level decreased';
      } else if (wasCorrect) {
        levelMessage = 'Learnt new item!';
      }

      setLevelChangeInfo({
        oldLevelDesc,
        newLevelDesc,
        isPositive,
        wasCorrect,
        message: levelMessage,
        xpGained: xpGained // Add XP gained to level change info
      });

      // Hide the level change info after a delay
      setTimeout(() => setLevelChangeInfo(null), 2500); // Increased to 2.5 seconds for better visibility
    }
  };

  /** Update review result for a correct answer. */
  const updateReviewResult = (uniqueCardId, cardId, isCorrect, attempts, newLevel) => {
    updateVariantStatus(uniqueCardId, {
      isCorrect,
      attempts,
      currentLevel: newLevel,
      wasActuallyAttempted: true,
      reviewStatus: isCorrect ? 'correct' : 'incorrect'
    });

    // Update the cards array for level display
    setCards((prev) =>
      prev.map((c) => {
        if (c.uniqueCardId === uniqueCardId || c.cardId === cardId) {
          return { ...c, currentLevel: newLevel ?? c.currentLevel };
        }
        return c;
      })
    );
  };

  /** Update review attempts for an incorrect answer. */
  const updateReviewAttempts = (uniqueCardId, cardId, attempts, newLevel) => {
    updateVariantStatus(uniqueCardId, {
      isCorrect: false,
      attempts,
      currentLevel: newLevel,
      wasActuallyAttempted: true,
      reviewStatus: 'incorrect'
    });

    // Update the cards array for level display
    setCards((prev) =>
      prev.map((c) => {
        if (c.uniqueCardId === uniqueCardId || c.cardId === cardId) {
          return { ...c, currentLevel: newLevel ?? c.currentLevel };
        }
        return c;
      })
    );
  };

  /** Post result to server. */
  const sendReviewResult = async (cardId, incorrectAttempts = 0, isCorrect = true, forcedLevel = null) => {
    try {
      if (!internalUserId) {
        if (token) {
          const decoded = jwtDecode(token);
          setInternalUserId(decoded.user._id);
        } else {
          console.error("No userId or token available for submitting review result");
          return { level: null, xpGained: 0, xpToNextLevel: 0 };
        }
      }

      const submission = {
        userId: internalUserId,
        cardId,
        deckId,
        incorrectAttempts,
        isCorrect,
      };

      // If a forced level is provided (for sentence completion), include it
      if (forcedLevel !== null) {
        submission.forcedLevel = forcedLevel;
      }

      // Add flashcard mapping data for course mode
      if (courseMode && courseId) {
        const currentCard = cards[currentIndex];
        const metadata = currentCard.metadata || {};

        if (metadata.sentenceId || metadata.conceptName) {
          submission.flashCardMapping = {
            userId: internalUserId,
            courseId,
            sentenceId: metadata.sentenceId,
            conceptName: metadata.conceptName,
            language: currentCourse.language
          };
        }
      }

      const response = await api.post(`/cards/submit-answer`, submission);

      // Handle level up information if present
      if (response.data.leveledUp) {
        setLevelUpInfo({
          leveledUp: true,
          levelTitle: response.data.levelTitle,
          oldLevelTitle: response.data.oldLevelTitle
        });

        // Auto-hide the level up banner after 5 seconds
        setTimeout(() => {
          setLevelUpInfo(null);
        }, 5000);
      }

      if (response.data.goalCompleted) {
        console.log('Goal completed! Launching fireworks animation');
        setShowFireworks(true);
        setTimeout(() => {
          setShowFireworks(false);
        }, 5000);
      }

      // FIXED: Return the forcedLevel if provided, otherwise get from response or use oldLevel as fallback
      const currentCard = cards[currentIndex];
      const level = forcedLevel !== null ? forcedLevel : response.data?.progress?.currentLevel ?? (currentCard?.currentLevel || 1);

      // Return an object with level and XP information
      return {
        level,
        xpGained: response.data.xpGained || 0,
        xpToNextLevel: response.data.xpToNextLevel || 0,
      };
    } catch (error) {
      console.error("Error updating review queue:", error);
      const currentCard = cards[currentIndex];
      return {
        level: forcedLevel !== null ? forcedLevel : (currentCard?.currentLevel || 1),
        xpGained: 0,
        xpToNextLevel: 0
      };
    }
  };

  const handlePermanentSkip = (card) => {
    const uniqueCardId = card.uniqueCardId || `${card.cardId}-${Math.random().toString(36).substring(2, 9)}`;
    const sentenceId = card.metadata?.sentenceId;

    // Add to skipped cards set
    setSkippedCards(prev => new Set([...prev, uniqueCardId]));

    // Remove from incorrect queue if it's there
    removeFromIncorrectQueue(uniqueCardId);

    // Mark this card as completed to update progress
    markCardCompleted(uniqueCardId);

    // UPDATED: Update variant status to skipped
    updateVariantStatus(uniqueCardId, {
      isCorrect: false,
      attempts: -1,
      wasActuallyAttempted: true,
      reviewStatus: 'skipped'
    });

    // FIXED: If this card is part of a sentence group, update with isSkipped = true
    if (sentenceId) {
      const sentenceResult = updateSentenceProgress(sentenceId, uniqueCardId, false, true); // ← isSkipped = true

      if (sentenceResult.shouldSubmit) {
        console.log(`SUBMITTING sentence ${sentenceId} via skip - all variants handled`);
        const oldLevel = card.currentLevel || 1;
        const sentenceHadErrors = (sentenceIncorrectAttempts[sentenceId] || 0) > 0;
        const finalLevel = determineNewLevel(oldLevel, sentenceHadErrors ? 1 : 0, true);

        sendReviewResult(card.cardId, sentenceHadErrors ? 1 : 0, true, finalLevel);
        setSubmittedSentences(prev => new Set([...prev, sentenceId]));
      }
    }

    // Recalculate progress
    setTimeout(() => recalcProgress(), 50);

    // Continue to the next card
    setWaitingToProceed(false);
    setIsSubmitted(false);
    setFeedback(null);
    setRevealedAnswer(null);
    setLevelChangeInfo(null);
    proceedToNextCard();
  };

  // Handler for early termination
  const handleEndEarly = () => {
    setConfirmExitOpen(true);
  };

  // MODIFIED: Handler to confirm early termination with clean data
  const handleConfirmEndEarly = () => {
    setConfirmExitOpen(false);
    if (onEndEarly) {
      // Generate clean summary data and pass to parent
      const cleanSummaryData = generateCleanSummaryData();
      onEndEarly(cleanSummaryData);
    }
  };

  // ADDED: Simple function to update a single variant's status
  const updateVariantStatus = (uniqueCardId, updates) => {
    setVariantAttempts(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(uniqueCardId);
      if (existing) {
        newMap.set(uniqueCardId, { ...existing, ...updates });
      }
      return newMap;
    });
  };

  // Determine current card and question type (default to "text").
  const currentCard = cards[currentIndex]; //trcbck

  // Handle different potential structures for card data
  let questionType = currentCard?.questionType || "text";

  // FIXED: Only change question type for non-audio/shadowing questions
  const isSentenceCard = !!currentCard?.metadata?.sentenceId;

  // Only change question type for sentence cards that aren't audio or shadowing
  if (tokenMode && isSentenceCard && questionType !== "audio" && questionType !== "shadowing") {
    // Only apply tokenMode to regular text questions that aren't audio or shadowing
    if (questionType === "text" || questionType === "tokenReorder") {
      questionType = tokenMode === "hard" ? "text" : "tokenReorder";
    }
  }

  // FIXED: Use the cardId directly as instructed
  const currentCardId = currentCard?.cardId;
  const currentAttempts = currentCardId ? (incorrectAttempts[currentCardId] || 0) : 0;

  // Extract question from card structure or use title as fallback
  const question = currentCard?.question ||
    currentCard?.cardId?.question ||
    (questionType === "tokenReorder" && !currentCard?.question ?
      `Arrange the following tokens to form a correct sentence:` :
      "") ||
    currentCard?.title ||
    currentCard?.cardId?.title ||
    "";

  // Calculate question length category for responsive font sizing
  const questionLength = question ?
    (question.length < 40 ? "short" :
      question.length < 100 ? "medium" : "long") : "medium";

  // Alert if no cards are available.
  const showNoCardsAlert = !reviewComplete && (cards.length === 0 || !currentCard);

  // Determine options for MCQ based on card structure
  const mcqOptions = currentCard?.choices ||
    currentCard?.cardId?.choices ||
    [];

  // Determine tokens for tokenReorder based on card structure 
  console.log('CurrentCard tokens ', currentCard?.tokens);
  const reorderTokens = currentCard?.tokens ||
    (questionType === "tokenReorder" && isSentenceCard && currentCard?.answer ?
      CardPreprocessor.tokenizeAndShuffle(currentCard.answer) :
      []);

  // Card level to display (handle potential undefined)
  const cardLevel = LEVEL_DESCRIPTIONS[currentCard?.currentLevel]
    || currentCard?.currentLevel ?
    `Level ${currentCard?.currentLevel}`
    : "Learning";

  const sentenceId = currentCard?.metadata?.sentenceId;
  const sentenceVariantInfo = sentenceId ? sentenceProgress[sentenceId] : null;
  const variantProgress = sentenceVariantInfo ?
    `${sentenceVariantInfo.correctVariants}/${sentenceVariantInfo.totalVariants} completed` : '';

  return (
    <div className="review-container">
      {/* Level Up Banner - New component */}
      {levelUpInfo && (
        <div className="level-up-banner">
          <div className="level-up-content">
            <div className="level-up-icon">🏆</div>
            <div className="level-up-text">
              <h3>Level Up!</h3>
              <p>{levelUpInfo.oldLevelTitle} → {levelUpInfo.levelTitle}</p>
            </div>
          </div>
        </div>
      )}

      <Container
        maxWidth="md"
        className="review-inner-container"
      >
        {levelChangeInfo && (
          <div className={`floating-level-change ${levelChangeInfo.isPositive ? "positive" : "negative"}`}>
            {levelChangeInfo.isPositive ? "⬆️" : "⬇️"}{" "}
            {levelChangeInfo.message || (levelChangeInfo.wasCorrect ? "Correct! " : "Incorrect! ")} New Level:{" "}
            {levelChangeInfo.newLevelDesc}
            {levelChangeInfo.xpGained > 0 && (
              <span className="xp-gained">+{levelChangeInfo.xpGained} XP</span>
            )}
          </div>
        )}

        {reviewComplete ? (
          <Paper className="review-complete">
            <Typography
              variant="h5"
              className="review-complete-title"
            >
              🎉 Review Complete! 🎉
            </Typography>

            <Alert
              severity="success"
              className="review-complete-alert"
            >
              You have successfully reviewed all cards!
            </Alert>

            <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
              Final streak: <b>{currentStreak}</b> in a row
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => onReviewComplete && onReviewComplete(generateCleanSummaryData())}
              className="review-continue-button"
            >
              Continue
            </Button>
          </Paper>
        ) : showNoCardsAlert ? (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              p: 3,
              fontSize: "1.1rem",
              borderRadius: "8px"
            }}
          >
            No cards available for review.
          </Alert>
        ) : (
          <Paper className="review-card">
            {/* Header section with title, squirrel, and progress */}
            <div className="card-header">
              {/* Early exit button */}
              {onEndEarly && (
                <Tooltip title="End review early and see summary">
                  <IconButton
                    className="exit-button"
                    onClick={handleEndEarly}
                    aria-label="End review early"
                  >
                    <ExitIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Progress display */}
              <div className="progress-display">
                <Typography className="progress-info">
                  {completedCount}/{originalCards.length} Cards
                </Typography>

                {currentStreak > 0 && (
                  <Chip
                    label={`${currentStreak >= 5 ? '🔥' : ''} ${currentStreak} in a row`}
                    color={currentStreak >= 10 ? "success" : "primary"}
                    className={`streak-chip ${pulseStreak ? "pulse" : ""}`}
                    size="small"
                  />
                )}

                {showStreakMessage && (
                  <Chip
                    label={streakMessage}
                    color="success"
                    size="small"
                    className="streak-message"
                  />
                )}
              </div>

              {/* Progress bar */}
              <div className="progress-wrapper">
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  className="progress-bar"
                  sx={{
                    height: '10px'
                  }}
                />
              </div>

              {/* Display sentence variant progress if available */}
              {sentenceVariantInfo && (
                <Typography variant="body2" className="sentence-progress">
                  Sentence Progress: {variantProgress}
                </Typography>
              )}
            </div>

            {/* Divider between header and content */}
            <Divider />

            {/* Question and answer section */}
            <div className={`card-content ${feedback?.type || ""}`}>
              {/* Question title */}
              <Typography
                variant="h5"
                className={`question-title ${questionLength}`}
                sx={{ fontWeight: 600 }}
              >
                {question || "Loading..."}
              </Typography>

              {/* Show incorrect attempts counter if any */}
              {currentAttempts > 0 && (
                <Typography
                  variant="subtitle2"
                  className="incorrect-attempts"
                >
                  Incorrect attempts: {currentAttempts}
                </Typography>
              )}

              {/* Replace Confetti with Fireworks */}
              <Fireworks trigger={showFireworks} />

              {/* Render appropriate answer input component based on question type */}
              {questionType === "mcq" ? (
                <MCQAnswerInput
                  key={currentIndex}
                  options={mcqOptions}
                  onSubmitAnswer={handleAnswerSubmit}
                  waitingToProceed={waitingToProceed}
                  onProceed={handleProceed}
                  correctAnswer={currentCard?.answer || currentCard?.cardId?.answer}
                />
              ) : questionType === "tokenReorder" ? (
                <ReorderTokensInput
                  key={currentIndex}
                  tokens={reorderTokens}
                  onSubmitAnswer={handleAnswerSubmit}
                  waitingToProceed={waitingToProceed}
                  onProceed={handleProceed}
                  correctAnswer={currentCard?.answer || currentCard?.cardId?.answer}
                  audioData={currentCard?.audioData || (currentCard?.audio?.path)}
                  question={question}
                />
              ) : questionType === "audio" ? (
                <AudioQuestion
                  key={currentIndex}
                  audioData={currentCard?.audioData || (currentCard?.audio?.path)}
                  tokens={reorderTokens}
                  question={question}
                  mode={currentCard?.audioMode || "transcribe"}
                  waitingToProceed={waitingToProceed}
                  onProceed={handleProceed}
                  onSubmitAnswer={handleAnswerSubmit}
                  correctAnswer={currentCard?.correctAudioAnswer ||
                    currentCard?.answer ||
                    currentCard?.cardId?.answer}
                />
              ) : questionType === "shadowing" ? (
                <ShadowingInput
                  audioData={currentCard.audioData}
                  audioCdnUrl={currentCard.audio.path ? currentCard.audio.path :
                    (currentCard.audioUrl ? currentCard.audioUrl : undefined)}
                  correctAnswer={currentCard.title}
                  language={currentCard.language || 'de-DE'}
                  onShadowingSubmit={handleShadowingSubmit}
                  waitingToProceed={waitingToProceed}
                  onProceed={handleProceed}
                  onRetry={() => {
                    // Retry is handled internally by ShadowingInput
                    console.log("Retry called from ShadowingInput");
                  }}
                  onSkip={() => {
                    // Mark this card as permanently skipped
                    handlePermanentSkip(currentCard);
                  }}
                />
              ) : (
                <TextAnswerInput
                  key={currentIndex}
                  // isSubmitted={isSubmitted}
                  onSubmitAnswer={handleAnswerSubmit}
                  waitingToProceed={waitingToProceed}
                  onProceed={handleProceed}
                  audioData={currentCard?.audioData || (currentCard?.audio?.path)}
                  question={question}
                  ref={inputRef}
                  isRetrying={isRetrying}
                />
              )}

              {/* Display correct answer when user answered incorrectly */}
              {revealedAnswer && (
                <div className="revealed-answer">
                  <Typography
                    variant="subtitle2"
                    color="error"
                    sx={{ fontWeight: "bold", mb: 1 }}
                    className="answer-label"
                  >
                    Correct Answer:
                  </Typography>
                  <Typography
                    variant="body1"
                    className="answer-text"
                  >
                    {revealedAnswer + '. '}
                    {currentCard.answerSynonyms?.length > 0 && (
                      <>
                        Other Accepted answers:
                        <ul>
                          {currentCard.answerSynonyms?.map((synonym, index) => (
                            <li key={index}>{synonym}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Typography>
                </div>
              )}
            </div>

            {/* Card footer with additional information */}
            <div className="card-footer">
              <Typography variant="body2" color="text.secondary">
                Card {currentIndex + 1} of {cards.length}
                {incorrectQueue.length > 0 && ` (+ ${incorrectQueue.length} to review again)`}
              </Typography>

              <Chip
                size="small"
                label={cardLevel}
                color={
                  (currentCard?.currentLevel || 1) <= 3 ? "default" :
                    (currentCard?.currentLevel || 1) <= 5 ? "primary" :
                      (currentCard?.currentLevel || 1) <= 7 ? "success" : "secondary"
                }
              />
            </div>
          </Paper>
        )}

        {!reviewComplete && cards.length > 0 && progress > 0 && (
          <Box
            sx={{
              textAlign: "center",
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {progress < 50 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Keep going! You're making good progress.
              </Typography>
            ) : progress < 80 ? (
              <Chip
                label="You're more than halfway there!"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "500" }}
              />
            ) : (
              <Chip
                label="Almost done! Just a few more cards to go!"
                color="success"
                variant="outlined"
                sx={{ fontWeight: "500" }}
              />
            )}
          </Box>
        )}

        {/* Confirmation Dialog for early termination */}
        <Dialog
          open={confirmExitOpen}
          onClose={() => setConfirmExitOpen(false)}
          aria-labelledby="end-review-dialog-title"
          aria-describedby="end-review-dialog-description"
          // TransitionComponent={Fade}
          transitionDuration={300}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '12px',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            id="end-review-dialog-title"
            sx={{
              backgroundColor: '#1a237e',
              color: 'white',
              padding: '16px 24px',
              fontWeight: 600
            }}
          >
            End Review Early?
          </DialogTitle>
          <DialogContent sx={{ padding: '24px', backgroundColor: 'white' }}>
            <br />
            <DialogContentText
              id="end-review-dialog-description"
              sx={{ color: '#444', fontSize: '1rem' }}
            >
              Are you sure you want to end this review session early? Your progress so far will be saved.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '16px 24px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: 'white'
            }}
          >
            <Button
              onClick={() => setConfirmExitOpen(false)}
              color="inherit"
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                padding: '8px 20px',
                fontWeight: 500
              }}
            >
              Continue Reviewing
            </Button>
            <Button
              onClick={handleConfirmEndEarly}
              variant="contained"
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                padding: '8px 20px',
                fontWeight: 600,
                backgroundColor: '#f44336',
                '&:hover': {
                  backgroundColor: '#d32f2f'
                }
              }}
            >
              End Review Early
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
};

export default ReviewMode;  